import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  FileText, Save, ArrowLeft, Check, AlertCircle, Globe, Search,
  Eye, EyeOff, Clock, Plus, Trash2, ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PAGE_ICONS = {
  homepage: '🏠', contact: '📞', 'slow-tourisme': '🌿', 
  club: '👑', reservation: '📅', navires: '⛵'
};

const AdminPages = () => {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const fetchPages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPages(data);
    } catch (err) {
      toast.error('Erreur chargement des pages');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const handleSave = async () => {
    if (!editingPage) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/pages/${editingPage.slug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editingPage.title,
          content: editingPage.content,
          meta_title: editingPage.meta_title,
          meta_description: editingPage.meta_description,
          is_active: editingPage.is_active
        })
      });
      if (res.ok) {
        toast.success('Page sauvegardée');
        await fetchPages();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (key, value) => {
    setEditingPage(prev => ({
      ...prev,
      content: { ...prev.content, [key]: value }
    }));
  };

  const addContentField = () => {
    const key = prompt('Nom du champ (ex: hero_image, section_text) :');
    if (key && key.trim()) {
      updateContent(key.trim(), '');
    }
  };

  const removeContentField = (key) => {
    setEditingPage(prev => {
      const newContent = { ...prev.content };
      delete newContent[key];
      return { ...prev, content: newContent };
    });
  };

  const filteredPages = pages.filter(p =>
    p.title?.toLowerCase().includes(search.toLowerCase()) ||
    p.slug?.toLowerCase().includes(search.toLowerCase())
  );

  // Editing view
  if (editingPage) {
    return (
      <AdminLayout title="Éditeur de contenu">
        <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-page-editor">
          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => setEditingPage(null)}
              className="text-[#4A5568]"
              data-testid="editor-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#2A9D8F] hover:bg-[#238577] text-white"
              data-testid="editor-save-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2"><Clock className="h-4 w-4 animate-spin" />Sauvegarde...</span>
              ) : (
                <span className="flex items-center gap-2"><Save className="h-4 w-4" />Sauvegarder</span>
              )}
            </Button>
          </div>

          {/* Page Meta */}
          <Card>
            <CardContent className="p-3 sm:p-6 space-y-4">
              <h3 className="font-display font-bold text-[#0A2540] flex items-center gap-2">
                <Globe className="h-5 w-5 text-[#2A9D8F]" />
                Informations de la page
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#4A5568] block mb-1">Titre de la page</label>
                  <input
                    type="text"
                    value={editingPage.title || ''}
                    onChange={(e) => setEditingPage(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
                    data-testid="editor-title"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#4A5568] block mb-1">Slug (URL)</label>
                  <input
                    type="text"
                    value={editingPage.slug || ''}
                    disabled
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-[#94A3B8]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#4A5568] block mb-1">Meta Title (SEO)</label>
                  <input
                    type="text"
                    value={editingPage.meta_title || ''}
                    onChange={(e) => setEditingPage(prev => ({ ...prev, meta_title: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
                    data-testid="editor-meta-title"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#4A5568] block mb-1">Meta Description (SEO)</label>
                  <input
                    type="text"
                    value={editingPage.meta_description || ''}
                    onChange={(e) => setEditingPage(prev => ({ ...prev, meta_description: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
                    data-testid="editor-meta-desc"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEditingPage(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    editingPage.is_active
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}
                  data-testid="editor-toggle-active"
                >
                  {editingPage.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  {editingPage.is_active ? 'Page active' : 'Page désactivée'}
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Content Fields */}
          <Card>
            <CardContent className="p-3 sm:p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-[#0A2540] flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#2A9D8F]" />
                  Contenu éditable
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addContentField}
                  className="text-[#2A9D8F] border-[#2A9D8F]/30 hover:bg-[#2A9D8F]/5"
                  data-testid="editor-add-field"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter un champ
                </Button>
              </div>

              <div className="space-y-4">
                {editingPage.content && Object.entries(editingPage.content).map(([key, value]) => (
                  <div key={key} className="group border border-slate-200 rounded-xl p-4 hover:border-[#2A9D8F]/30 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-[#0A2540]">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </label>
                      <button
                        onClick={() => removeContentField(key)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-all"
                        title="Supprimer ce champ"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-2 font-mono">{key}</p>
                    {typeof value === 'string' && value.length > 100 ? (
                      <textarea
                        value={value}
                        onChange={(e) => updateContent(key, e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent text-sm"
                        data-testid={`editor-field-${key}`}
                      />
                    ) : typeof value === 'boolean' ? (
                      <button
                        onClick={() => updateContent(key, !value)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${value ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
                        data-testid={`editor-field-${key}`}
                      >
                        {value ? 'Oui' : 'Non'}
                      </button>
                    ) : typeof value === 'number' ? (
                      <input
                        type="number"
                        value={value}
                        onChange={(e) => updateContent(key, Number(e.target.value))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent text-sm"
                        data-testid={`editor-field-${key}`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={typeof value === 'string' ? value : JSON.stringify(value)}
                        onChange={(e) => updateContent(key, e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent text-sm"
                        data-testid={`editor-field-${key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  // List view
  return (
    <AdminLayout title="Gestion du contenu">
      <div className="space-y-6" data-testid="admin-pages">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-[#4A5568]">
            Modifiez le contenu de toutes les pages de votre site depuis cette interface.
          </p>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
              data-testid="pages-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#94A3B8]">Chargement...</div>
        ) : filteredPages.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#4A5568]">Aucune page trouvée</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPages.map((page) => (
              <Card 
                key={page.slug}
                className="hover:shadow-lg transition-all cursor-pointer border-slate-200 hover:border-[#2A9D8F]/40"
                onClick={() => setEditingPage(page)}
                data-testid={`page-card-${page.slug}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-3xl">{PAGE_ICONS[page.slug] || '📄'}</div>
                    <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      page.is_active
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {page.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-[#0A2540] text-lg mb-1">{page.title}</h3>
                  <p className="text-sm text-[#94A3B8] font-mono mb-3">/{page.slug}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#94A3B8]">
                      {page.content ? Object.keys(page.content).length : 0} champs
                    </p>
                    <div className="flex items-center gap-1 text-[#2A9D8F] text-sm font-medium">
                      Modifier <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                  {page.updated_at && (
                    <p className="text-xs text-[#94A3B8] mt-2 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(page.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
